import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Upload, Button, Card, Alert, Spin, Result, Steps, message, Form, Input, Select, Image, Row, Col, Space, Progress, Modal, Tag } from 'antd';
import { InboxOutlined, CheckCircleOutlined, EditOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import { getAllCountries } from '../utils/countryCode';
import { validatePassportName, getNameFormatHint } from '../utils/nameValidator';
import Footer from '../components/Footer';
import './UploadPage.css';

const { Dragger } = Upload;
const { Step } = Steps;
const { Option } = Select;

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

// 格式化日期为YYYY-MM-DD
const formatDate = (date) => {
  if (!date) return '';
  // 处理DD/MM/YYYY格式
  if (typeof date === 'string' && date.includes('/')) {
    const parts = date.split('/');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
    }
  }
  // 处理ISO日期格式
  return moment(date).format('YYYY-MM-DD');
};

// 证件类型选项（日游使用）
const DOCUMENT_TYPE_OPTIONS = [
  { value: 'passport', label: '护照/Passport' },
  { value: 'nz_driver_license', label: '新西兰驾照/NZ Driver Licence' },
  { value: 'nz_senior_card', label: '新西兰老年卡/NZ SuperGold Card' },
];

// 根据证件类型判断字段显示规则（日游）
const getDaytourFieldVisibility = (documentType) => {
  switch (documentType) {
    case 'nz_driver_license':
      return {
        showGender: false,
        showNationality: false,
        showIssueDate: false,
        showExpiryDate: false,
        showBirthPlace: false,
        showBirthDate: true,
      };
    case 'nz_senior_card':
      return {
        showGender: false,
        showNationality: false,
        showIssueDate: false,
        showExpiryDate: false,
        showBirthPlace: false,
        showBirthDate: false,
      };
    case 'passport':
    default:
      return {
        showGender: true,
        showNationality: true,
        showIssueDate: true,
        showExpiryDate: true,
        showBirthPlace: true,
        showBirthDate: true,
      };
  }
};

function UploadPage() {
  const { uploadLink } = useParams();
  const [form] = Form.useForm();
  const [touristInfo, setTouristInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tempImageUrl, setTempImageUrl] = useState(null);
  const [tempImageFile, setTempImageFile] = useState(null);
  const [recognizedData, setRecognizedData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [finalSubmitted, setFinalSubmitted] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [passportExpiryWarning, setPassportExpiryWarning] = useState(null);
  const [expiryModalVisible, setExpiryModalVisible] = useState(false);
  const [nameMismatchWarning, setNameMismatchWarning] = useState(null);

  // 日游专用状态
  const [documentType, setDocumentType] = useState('passport');
  const [discountAlert, setDiscountAlert] = useState(null); // { eligible: boolean, age: number }

  // ── 中国团附加证件（身份证 / 台胞证）──
  // chinaDocState: { required, docType, docLabel, uploaded, ... }，由后端按「本团要求 + 护照国籍」算出
  const [chinaDocState, setChinaDocState] = useState(null);
  const [chinaDocForm] = Form.useForm();
  const [chinaDocFile, setChinaDocFile] = useState(null);
  const [chinaDocPreviewUrl, setChinaDocPreviewUrl] = useState(null);
  const [chinaDocRecognizing, setChinaDocRecognizing] = useState(false);
  const [chinaDocSubmitting, setChinaDocSubmitting] = useState(false);
  const [chinaDocWarnings, setChinaDocWarnings] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      await fetchTouristInfo();
      await checkUploadStatus();
    };
    fetchData();
  }, [uploadLink]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTouristInfo = async () => {
    try {
      const response = await axios.get(`${API_BASE}/tourists/link/${uploadLink}`);
      // 存储完整的响应数据，包含 tourType
      setTouristInfo(response.data.data);
      setLoading(false);

      // 中国团附加证件：护照已传但还欠身份证/台胞证 → 直接进第 3 步，不用重传护照
      const chinaDoc = response.data.data.chinaDoc;
      setChinaDocState(chinaDoc || null);
      if (
        response.data.data.uploadStatus !== 'verified' &&
        chinaDoc?.required && !chinaDoc.uploaded && response.data.data.passportNumber
      ) {
        setCurrentStep(2);
        return;
      }

      // 如果已经上传过，显示已完成状态
      if (response.data.data.uploadStatus === 'verified') {
        // 从touristInfo中提取数据
        const submittedData = {
          fullName: response.data.data.passportName || response.data.data.touristName,
          passportNumber: response.data.data.passportNumber,
          gender: response.data.data.gender || 'M',
          expiryDate: response.data.data.passportExpiryDate,
          nationality: response.data.data.nationality
        };
        setRecognizedData(submittedData);
        setFinalSubmitted(true);
        setCurrentStep(2);
      }
    } catch (error) {
      console.error('Failed to fetch tourist info:', error);
      setLoading(false);
    }
  };

  const checkUploadStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE}/upload/status/${uploadLink}`);
      if (response.data.data.status === 'verified') {
        setFinalSubmitted(true);
        setCurrentStep(2);
        // 格式化数据，确保fullName字段存在
        const recognizedData = response.data.data.recognizedData || {};
        if (recognizedData.name && !recognizedData.fullName) {
          recognizedData.fullName = recognizedData.name;
        }
        setRecognizedData(recognizedData);
      }
    } catch (error) {
      console.error('Failed to check upload status:', error);
    }
  };

  // 检查优惠价资格（一日游专用，55岁以上）
  const checkDiscountEligibility = (birthDateStr) => {
    if (!touristInfo?.discountPrice || touristInfo?.tourType !== 'daytour') {
      setDiscountAlert(null);
      return;
    }
    const departureDate = touristInfo?.departureDate;
    if (!birthDateStr || !departureDate) {
      setDiscountAlert(null);
      return;
    }
    const birthMoment = moment(birthDateStr, ['YYYY-MM-DD', 'DD/MM/YYYY', 'YYYY/MM/DD']);
    const departureMoment = moment(departureDate);
    if (!birthMoment.isValid() || !departureMoment.isValid()) {
      setDiscountAlert(null);
      return;
    }
    const age = departureMoment.diff(birthMoment, 'years');
    setDiscountAlert({ eligible: age >= 55, age });
  };

  // 姓名输入：拼接姓和名，自动加 /
  const handleSurnameChange = (e) => {
    const surname = e.target.value.toUpperCase();
    const givenName = form.getFieldValue('givenNameInput') || '';
    form.setFieldsValue({ surnameInput: surname, fullName: surname && givenName ? `${surname}/${givenName}` : '' });
  };
  const handleGivenNameChange = (e) => {
    const givenName = e.target.value.toUpperCase();
    const surname = form.getFieldValue('surnameInput') || '';
    form.setFieldsValue({ givenNameInput: givenName, fullName: surname && givenName ? `${surname}/${givenName}` : '' });
  };

  // 检查OCR识别姓名与销售录入姓名是否一致
  const checkNameMismatch = (ocrName) => {
    const originalName = touristInfo?.touristName;
    if (!originalName || !ocrName) return;
    const normalize = (n) => n.toUpperCase().replace(/\s+/g, '').replace(/-/g, '').trim();
    if (normalize(originalName) !== normalize(ocrName)) {
      setNameMismatchWarning({ originalName, ocrName });
    } else {
      setNameMismatchWarning(null);
    }
  };

  // 检查护照有效期是否距离出发日期小于6个月
  const checkPassportExpiry = (expiryDateStr) => {
    const departureDate = touristInfo?.departureDate;
    if (!expiryDateStr || !departureDate) {
      setPassportExpiryWarning(null);
      return;
    }

    const expiryMoment = moment(expiryDateStr, ['YYYY-MM-DD', 'DD/MM/YYYY', 'YYYY/MM/DD']);
    const departureMoment = moment(departureDate);

    if (expiryMoment.isValid() && departureMoment.isValid()) {
      const monthsDiff = expiryMoment.diff(departureMoment, 'months', true);
      if (monthsDiff < 6) {
        setPassportExpiryWarning({
          expiryDate: expiryMoment.format('YYYY-MM-DD'),
          departureDate: departureMoment.format('YYYY-MM-DD'),
          monthsDiff: Math.max(0, monthsDiff).toFixed(1)
        });
        setExpiryModalVisible(true);
        return;
      }
    }
    setPassportExpiryWarning(null);
  };

  // ─── 标准团（standard）上传处理 ─────────────────────────────────────────────
  const handleUpload = async (file) => {
    // 先创建预览
    const reader = new FileReader();
    reader.onload = (e) => {
      setTempImageUrl(e.target.result);
    };
    reader.readAsDataURL(file);
    setTempImageFile(file);

    // 该销售已禁用 OCR：跳过自动识别，直接进入手动输入步骤
    if (touristInfo?.ocrDisabled) {
      setUploading(false);
      setRecognizedData({ ocrDisabled: true });
      form.setFieldsValue({
        // 销售已录入的姓名作为初始
        fullName: touristInfo.touristName || '',
        nationality: 'CHN'
      });
      setCurrentStep(1);
      setIsEditing(true);
      message.info('请手动输入护照信息（OCR 自动识别已关闭）');
      return;
    }

    const formData = new FormData();
    formData.append('passport', file);
    formData.append('preview', 'true'); // 告诉后端这只是预览

    setUploading(true);
    setUploadProgress(0);

    // 更真实的进度条模拟
    const progressSteps = [
      { percent: 20, text: '正在上传图片...' },
      { percent: 40, text: '图片质量检查中...' },
      { percent: 60, text: 'AI智能识别中...' },
      { percent: 80, text: '解析护照信息...' },
      { percent: 95, text: '完成识别处理...' }
    ];

    let stepIndex = 0;
    const progressInterval = setInterval(() => {
      if (stepIndex < progressSteps.length) {
        setUploadProgress(progressSteps[stepIndex].percent);
        setProgressText(progressSteps[stepIndex].text);
        stepIndex++;
      } else {
        clearInterval(progressInterval);
      }
    }, 800);

    try {
      const response = await axios.post(
        `${API_BASE}/upload/passport/preview/${uploadLink}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        // 完成进度条
        clearInterval(progressInterval);
        setUploadProgress(100);
        setProgressText('识别完成！');

        setRecognizedData(response.data.data);

        // 设置表单默认值
        let nationality = response.data.data.nationality || 'CHN';
        // 确保CHI被转换为CHN
        if (nationality === 'CHI') {
          nationality = 'CHN';
        }

        const ocrName = response.data.data.recognizedName || '';
        const nameParts = ocrName.split('/');
        const formValues = {
          fullName: ocrName,
          surnameInput: (nameParts[0] || '').trim(),
          givenNameInput: (nameParts[1] || '').trim(),
          passportNumber: response.data.data.passportNumber || '',
          gender: response.data.data.gender || 'M',
          issueDate: response.data.data.issueDate || '',
          expiryDate: response.data.data.expiryDate || '',
          birthDate: response.data.data.birthDate || '',
          birthPlace: response.data.data.birthPlace || '',
          nationality: nationality
        };

        form.setFieldsValue(formValues);
        setCurrentStep(1);
        setIsEditing(true);

        // 检查护照有效期是否距离出发日期小于6个月
        checkPassportExpiry(response.data.data.expiryDate);

        // 检查OCR姓名与销售录入姓名是否一致
        checkNameMismatch(response.data.data.recognizedName);

        message.success('护照识别成功，请确认信息！');
      }
    } catch (error) {
      // 处理错误 - 简化错误提示
      clearInterval(progressInterval);
      // 后端因销售禁用 OCR 拒绝时，引导手动输入
      if (error.response?.status === 403 && error.response?.data?.error === 'OCR_DISABLED') {
        setRecognizedData({ ocrDisabled: true });
        form.setFieldsValue({
          fullName: touristInfo?.touristName || '',
          nationality: 'CHN'
        });
        setCurrentStep(1);
        setIsEditing(true);
        message.info(error.response.data.message || '请手动输入护照信息');
        return;
      }
      const isIncomplete = error.response?.data?.error === 'INCOMPLETE_PASSPORT' || error.response?.data?.error === 'MRZ_UNVERIFIED';
      const errorMessage = error.response?.data?.message || error.response?.data?.error || '识别失败，请重试';
      message.error(errorMessage, isIncomplete ? 8 : 3);
      setCurrentStep(0);
      setTempImageUrl(null);
      setTempImageFile(null);
      setUploadProgress(0);
      setProgressText('');
    } finally {
      setUploading(false);
    }

    return false;
  };

  // ─── 日游（daytour）上传处理：直接保存，不做OCR识别 ───────────────────────────
  const handleDaytourUpload = async (file) => {
    setUploading(true);

    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', documentType);

    try {
      const response = await axios.post(
        `${API_BASE}/upload/document/${uploadLink}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        if (response.data.data) {
          setRecognizedData(response.data.data);
        } else if (response.data.recognizedName) {
          setRecognizedData({ fullName: response.data.recognizedName });
        }
        setFinalSubmitted(true);
        setCurrentStep(2);
        message.success(response.data.recognizedName
          ? `证件已上传，姓名已识别：${response.data.recognizedName}`
          : '证件已上传成功！');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || '上传失败，请重试';
      message.error(errorMessage);
    } finally {
      setUploading(false);
    }

    return false;
  };

  // ─── 标准团最终提交 ───────────────────────────────────────────────────────────
  const handleFinalSubmit = async () => {
    try {
      const values = await form.validateFields();

      // 从姓名分栏拼接 fullName
      const surname = (values.surnameInput || '').trim().toUpperCase();
      const givenName = (values.givenNameInput || '').trim().toUpperCase();
      values.fullName = `${surname}/${givenName}`;

      // 验证并格式化姓名
      const nameValidation = validatePassportName(values.fullName);
      if (!nameValidation.valid) {
        message.error(nameValidation.error);
        return;
      }

      // 填了邮箱则需要验证
      if (values.contactEmail && !emailVerified) {
        message.error('请先验证邮箱');
        return;
      }

      setConfirming(true);

      const formData = new FormData();
      formData.append('passport', tempImageFile);
      formData.append('confirmData', JSON.stringify({
        fullName: nameValidation.formatted,
        passportNumber: values.passportNumber,
        gender: values.gender,
        issueDate: values.issueDate,
        expiryDate: values.expiryDate,
        birthDate: values.birthDate,
        birthPlace: values.birthPlace,
        nationality: values.nationality,
        contactPhone: values.contactPhone,
        contactEmail: values.contactEmail
      }));

      const response = await axios.post(
        `${API_BASE}/upload/passport/${uploadLink}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        // 保存提交的数据用于显示
        const submittedData = {
          fullName: nameValidation.formatted,
          passportNumber: values.passportNumber,
          gender: values.gender,
          expiryDate: values.expiryDate,
          birthDate: values.birthDate,
          nationality: values.nationality
        };
        setRecognizedData(submittedData);

        // 中国团 + 中国/台湾护照：护照传完还要再传身份证/台胞证，先别显示完成页
        const chinaDoc = response.data.chinaDoc;
        setChinaDocState(chinaDoc || null);
        if (chinaDoc?.required && !chinaDoc.uploaded) {
          setCurrentStep(2);
          message.success(`护照已保存，请继续上传${chinaDoc.docLabel}`);
        } else {
          setFinalSubmitted(true);
          setCurrentStep(2);
          message.success('护照信息已确认并保存！');
        }
      }
    } catch (error) {
      if (error.response) {
        message.error(error.response.data.error || '提交失败，请重试');
      } else if (error.errorFields) {
        message.error('请填写所有必填项');
      } else {
        message.error('提交失败，请重试');
      }
    } finally {
      setConfirming(false);
    }
  };

  // ─── 日游最终提交 ─────────────────────────────────────────────────────────────
  const handleDaytourFinalSubmit = async () => {
    try {
      const values = form.getFieldsValue();

      // 从姓名分栏拼接 fullName
      const surname = (values.surnameInput || '').trim().toUpperCase();
      const givenName = (values.givenNameInput || '').trim().toUpperCase();
      values.fullName = `${surname}/${givenName}`;

      // 验证并格式化姓名
      const nameValidation = validatePassportName(values.fullName);
      if (!nameValidation.valid) {
        message.error(nameValidation.error);
        return;
      }

      // 验证邮箱是否已验证（驾照邮箱选填，未填则不需要验证）
      const needEmailVerify = isEmailRequired || values.contactEmail;
      if (needEmailVerify && !emailVerified) {
        message.error('请先验证邮箱');
        return;
      }

      setConfirming(true);

      const formData = new FormData();
      formData.append('document', tempImageFile);
      formData.append('documentType', documentType);
      formData.append('confirmData', JSON.stringify({
        fullName: nameValidation.formatted,
        documentNumber: values.documentNumber,
        gender: values.gender || '',
        nationality: values.nationality || '',
        birthDate: values.birthDate || '',
        issueDate: values.issueDate || '',
        expiryDate: values.expiryDate || '',
        birthPlace: values.birthPlace || '',
        contactEmail: values.contactEmail,
        phone: touristInfo?.phone || values.phone || '',
      }));

      const response = await axios.post(
        `${API_BASE}/upload/document/${uploadLink}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        const submittedData = {
          fullName: nameValidation.formatted,
          documentNumber: values.documentNumber,
          birthDate: values.birthDate,
          expiryDate: values.expiryDate || '',
        };
        setRecognizedData(submittedData);
        setFinalSubmitted(true);
        setCurrentStep(2);
        message.success('证件信息已确认并保存！');
      }
    } catch (error) {
      if (error.response) {
        message.error(error.response.data.error || '提交失败，请重试');
      } else if (error.errorFields) {
        message.error('请填写所有必填项');
      } else {
        message.error('提交失败，请重试');
      }
    } finally {
      setConfirming(false);
    }
  };

  // ─── 中国团附加证件：选图 → OCR 识别 → 确认提交 ─────────────────────────────
  const handleChinaDocSelect = async (file) => {
    setChinaDocFile(file);
    setChinaDocWarnings([]);
    chinaDocForm.resetFields();

    const reader = new FileReader();
    reader.onload = (e) => setChinaDocPreviewUrl(e.target.result);
    reader.readAsDataURL(file);

    setChinaDocRecognizing(true);
    try {
      const formData = new FormData();
      formData.append('chinaDoc', file);
      const res = await axios.post(`${API_BASE}/upload/china-doc/preview/${uploadLink}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        const d = res.data.data;
        chinaDocForm.setFieldsValue({
          fullName: d.fullName || '',
          documentNumber: d.documentNumber || '',
          expiryDate: d.expiryDate || '',
        });
        setChinaDocWarnings(res.data.warnings || []);
        message.success(`${d.docLabel}识别成功，请核对信息后提交`);
      }
    } catch (error) {
      // 识别失败不挡路：允许手动填写后提交（照片已选好，仍可上传）
      message.warning(error.response?.data?.error || '未能自动识别，请手动填写证件信息');
    } finally {
      setChinaDocRecognizing(false);
    }
  };

  const handleChinaDocSubmit = async () => {
    if (!chinaDocFile) {
      message.error('请先上传证件照片');
      return;
    }
    try {
      const values = await chinaDocForm.validateFields();
      setChinaDocSubmitting(true);
      const formData = new FormData();
      formData.append('chinaDoc', chinaDocFile);
      formData.append('confirmData', JSON.stringify({
        fullName: values.fullName,
        documentNumber: values.documentNumber,
        expiryDate: values.expiryDate || null,
      }));
      const res = await axios.post(`${API_BASE}/upload/china-doc/${uploadLink}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        setChinaDocState((prev) => ({ ...(prev || {}), uploaded: true }));
        setFinalSubmitted(true);
        setCurrentStep(3);
        message.success(res.data.message || '证件上传成功');
      }
    } catch (error) {
      if (error.errorFields) message.error('请填写所有必填项');
      else message.error(error.response?.data?.error || '提交失败，请重试');
    } finally {
      setChinaDocSubmitting(false);
    }
  };

  const handleChinaDocReset = () => {
    setChinaDocFile(null);
    setChinaDocPreviewUrl(null);
    setChinaDocWarnings([]);
    chinaDocForm.resetFields();
  };

  const handleReset = () => {
    setCurrentStep(0);
    setTempImageUrl(null);
    setTempImageFile(null);
    setRecognizedData(null);
    setIsEditing(false);
    setUploadProgress(0);
    setProgressText('');
    setEmailVerified(false);
    setCountdown(0);
    setNameMismatchWarning(null);
    form.resetFields();
  };

  // 发送验证码
  const handleSendVerificationCode = async () => {
    try {
      const email = form.getFieldValue('contactEmail');
      if (!email) {
        message.error('请先输入邮箱地址');
        return;
      }

      setSendingCode(true);
      const response = await axios.post(`${API_BASE}/email-verification/send-code`, {
        email,
        uploadLink
      });

      if (response.data.success) {
        message.success('验证码已发送，请查收邮件');
        setCountdown(60);
        const timer = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (error) {
      message.error(error.response?.data?.error || '发送验证码失败');
    } finally {
      setSendingCode(false);
    }
  };

  // 验证验证码
  const handleVerifyCode = async () => {
    try {
      const email = form.getFieldValue('contactEmail');
      const verificationCode = form.getFieldValue('verificationCode');

      if (!email || !verificationCode) {
        message.error('请输入邮箱和验证码');
        return;
      }

      setVerifyingCode(true);
      const response = await axios.post(`${API_BASE}/email-verification/verify-code`, {
        email,
        uploadLink,
        code: verificationCode
      });

      if (response.data.success) {
        setEmailVerified(true);
        message.success('邮箱验证成功');
      }
    } catch (error) {
      message.error(error.response?.data?.error || '验证码验证失败');
    } finally {
      setVerifyingCode(false);
    }
  };

  // 标准团 upload props（保持不变）
  const uploadProps = {
    name: 'passport',
    multiple: false,
    accept: 'image/*',
    beforeUpload: handleUpload,
    showUploadList: false,
  };

  // 日游 upload props
  const daytourUploadProps = {
    name: 'document',
    multiple: false,
    accept: 'image/*',
    beforeUpload: handleDaytourUpload,
    showUploadList: false,
  };

  // 派生状态
  const isDaytour = touristInfo?.tourType === 'daytour';
  const fieldVisibility = isDaytour ? getDaytourFieldVisibility(documentType) : null;
  // 中国团 + 中国/台湾护照：护照之后还要传身份证/台胞证，步骤条多一格
  const needChinaDocStep = !isDaytour && !!chinaDocState?.required;

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  if (!touristInfo) {
    return (
      <Result
        status="404"
        title="404"
        subTitle="无效的上传链接"
      />
    );
  }

  // ─── 已提交完成页面 ───────────────────────────────────────────────────────────
  if (finalSubmitted) {
    return (
      <div className="upload-page">
        <Card className="upload-card">
          <Result
            status="success"
            title={isDaytour ? '证件信息已提交！' : '护照信息已提交！'}
            subTitle={
              isDaytour
                ? '您的证件信息已成功验证并保存'
                : (chinaDocState?.uploaded
                  ? `您的护照与${chinaDocState.docLabel || '证件'}已成功验证并保存`
                  : '您的护照信息已成功验证并保存')
            }
            extra={isDaytour ? null : (
              recognizedData && (
                <div className="recognized-info">
                  <h3>提交的信息：</h3>
                  <p><strong>姓名：</strong>{recognizedData.fullName || recognizedData.name || ''}</p>
                  <p><strong>护照号：</strong>{recognizedData.passportNumber || ''}</p>
                  <p><strong>性别：</strong>{recognizedData.gender === 'M' ? '男' : '女'}</p>
                  <p><strong>护照有效期至：</strong>{formatDate(recognizedData.expiryDate)}</p>
                </div>
              )
            )}
          />
          <Footer />
        </Card>
      </div>
    );
  }

  // ─── 共用邮箱验证区块（标准团与日游均使用）────────────────────────────────────
  const isEmailRequired = false;
  const renderEmailVerification = () => (
    <>
      <Form.Item
        name="contactEmail"
        label={isEmailRequired ? '联系邮箱' : '联系邮箱（选填）'}
        rules={[
          ...(isEmailRequired ? [{ required: true, message: '请输入联系邮箱' }] : []),
          { type: 'email', message: '邮箱格式不正确' }
        ]}
      >
        <Input
          placeholder="请输入联系邮箱"
          suffix={
            emailVerified ?
              <CheckCircleOutlined style={{ color: '#52c41a' }} /> :
              null
          }
        />
      </Form.Item>

      {/* 邮箱验证码 */}
      <Row gutter={[16, 0]} align="bottom">
        <Col xs={24} sm={12}>
          <Form.Item
            name="verificationCode"
            label="邮箱验证码"
            rules={[
              { required: isEmailRequired && !emailVerified, message: '请输入验证码' }
            ]}
          >
            <Input
              placeholder="请输入6位验证码"
              maxLength={6}
              disabled={emailVerified}
              suffix={
                emailVerified ?
                  <CheckCircleOutlined style={{ color: '#52c41a' }} /> :
                  null
              }
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label=" " className="email-verify-actions">
            <Space wrap>
              <Button
                onClick={handleSendVerificationCode}
                loading={sendingCode}
                disabled={countdown > 0 || emailVerified}
              >
                {countdown > 0 ? `${countdown}s后重发` : '发送验证码'}
              </Button>
              <Button
                type="primary"
                onClick={handleVerifyCode}
                loading={verifyingCode}
                disabled={emailVerified}
              >
                验证
              </Button>
            </Space>
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  // ─── 提交按钮区块 ─────────────────────────────────────────────────────────────
  const renderSubmitButtons = (onSubmit) => {
    // 驾照邮箱选填，未填邮箱时无需验证即可提交
    const needEmailVerify = isEmailRequired || form.getFieldValue('contactEmail');
    const canSubmit = !needEmailVerify || emailVerified;
    return (
    <Form.Item className="submit-buttons">
      <Space size="middle" wrap>
        <Button
          type="primary"
          size="large"
          icon={<CheckCircleOutlined />}
          onClick={onSubmit}
          loading={confirming}
          disabled={!canSubmit}
        >
          {canSubmit ? '确认无误，提交信息' : '请先验证邮箱'}
        </Button>
        <Button
          size="large"
          icon={<ReloadOutlined />}
          onClick={handleReset}
        >
          重新上传
        </Button>
      </Space>
    </Form.Item>
  );
  };

  // ─── 日游确认信息表单 ─────────────────────────────────────────────────────────
  const renderDaytourConfirmForm = () => {
    const { showGender, showNationality, showIssueDate, showExpiryDate, showBirthPlace, showBirthDate } = fieldVisibility;

    return (
      <div className="confirmation-section">
        <Row gutter={24}>
          <Col xs={24} md={10}>
            <div className="passport-image-section">
              <h3>证件照片</h3>
              {tempImageUrl && (
                <Image
                  src={tempImageUrl}
                  alt="证件照片"
                  style={{ width: '100%', maxHeight: '400px', objectFit: 'contain' }}
                />
              )}
            </div>
          </Col>
          <Col xs={24} md={14}>
            <div className="passport-info-section">
              <h3>
                <EditOutlined /> 请确认或修改证件信息
              </h3>
              {nameMismatchWarning && (
                <Alert
                  type="warning"
                  showIcon
                  message="姓名不一致提醒"
                  description={
                    <div>
                      <div>销售录入姓名：<strong>{nameMismatchWarning.originalName}</strong></div>
                      <div>护照识别姓名：<strong>{nameMismatchWarning.ocrName}</strong></div>
                      <div style={{ marginTop: 8, color: '#666' }}>
                        请核对护照信息是否正确。如果护照姓名与销售录入的不一致，请联系销售人员确认。
                      </div>
                    </div>
                  }
                  style={{ marginBottom: 16 }}
                />
              )}
              <Form
                form={form}
                layout="vertical"
                className="passport-form"
              >
                {/* 姓名 */}
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="surnameInput"
                      label="姓 / Surname"
                      rules={[{ required: true, message: '请输入姓' }]}
                    >
                      <Input placeholder="例: ZHANG" onChange={handleSurnameChange} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="givenNameInput"
                      label="名 / Given Name"
                      rules={[{ required: true, message: '请输入名' }]}
                    >
                      <Input placeholder="例: SAN" onChange={handleGivenNameChange} />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="fullName" hidden><Input /></Form.Item>

                {/* 证件号码 */}
                <Form.Item
                  name="documentNumber"
                  label="证件号码"
                  rules={[{ required: true, message: '请输入证件号码' }]}
                >
                  <Input placeholder="请输入证件号码" style={{ textTransform: 'uppercase' }} />
                </Form.Item>

                {/* 性别 & 国籍（按证件类型显示） */}
                {(showGender || showNationality) && (
                  <Row gutter={16}>
                    {showGender && (
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="gender"
                          label="性别"

                          rules={[{ required: true, message: '请选择性别' }]}
                        >
                          <Select>
                            <Option value="M">男</Option>
                            <Option value="F">女</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    )}
                    {showNationality && (
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="nationality"
                          label="国籍"

                          rules={[{ required: true, message: '请选择国籍' }]}
                        >
                          <Select
                            placeholder="请选择国籍"
                            showSearch
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                              option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                          >
                            {getAllCountries().map(country => (
                              <Option key={country.value} value={country.value} label={country.label}>
                                {country.label}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                )}

                {/* 出生日期 & 签发日期 */}
                {(showBirthDate || showIssueDate) && (
                  <Row gutter={16}>
                    {showBirthDate && (
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="birthDate"
                          label="出生日期"

                          rules={[{ required: true, message: '请输入出生日期' }]}
                        >
                          <Input
                            placeholder="DD/MM/YYYY"
                            disabled
                          />
                        </Form.Item>
                      </Col>
                    )}
                    {showIssueDate && (
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="issueDate"
                          label="证件签发日期"

                        >
                          <Input placeholder="DD/MM/YYYY" />
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                )}
                {discountAlert && (
                  discountAlert.eligible ? (
                    <Alert
                      message="符合优惠价条件"
                      description={`出团时年龄 ${discountAlert.age} 岁，满足55岁以上优惠价条件。`}
                      type="success"
                      showIcon
                      style={{ marginBottom: 16, marginTop: -8 }}
                    />
                  ) : (
                    <Alert
                      message="不满足优惠价格条件，需要补款"
                      description={`出团时年龄 ${discountAlert.age} 岁，不满55岁，无法享受优惠价。请联系销售人员了解补款事宜。`}
                      type="warning"
                      showIcon
                      style={{ marginBottom: 16, marginTop: -8 }}
                    />
                  )
                )}

                {/* 有效期（按证件类型显示） */}
                {showExpiryDate && (
                  <>
                    <Form.Item
                      name="expiryDate"
                      label="证件有效期"
                      rules={[{ required: true, message: '请输入证件有效期' }]}
                    >
                      <Input
                        placeholder="DD/MM/YYYY"
                        onChange={(e) => {
                          if (documentType === 'passport') {
                            checkPassportExpiry(e.target.value);
                          }
                        }}
                      />
                    </Form.Item>
                    {passportExpiryWarning && documentType === 'passport' && (
                      <Tag color="warning" style={{ marginBottom: 16, marginTop: -8, cursor: 'pointer' }} onClick={() => setExpiryModalVisible(true)}>
                        <WarningOutlined /> 护照有效期不足6个月（剩余 {passportExpiryWarning.monthsDiff} 个月）
                      </Tag>
                    )}
                  </>
                )}

                {/* 出生地（按证件类型显示） */}
                {showBirthPlace && (
                  <Form.Item
                    name="birthPlace"
                    label="出生地（选填）"
                    rules={[
                      {
                        pattern: /^[A-Za-z\s-]+$/,
                        message: '出生地只能包含英文字母、空格和横线'
                      }
                    ]}
                    extra="选填，英文格式，例如：BEIJING"
                  >
                    <Input
                      placeholder="请输入出生地（英文）"
                      style={{ textTransform: 'uppercase' }}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        form.setFieldsValue({ birthPlace: value });
                      }}
                    />
                  </Form.Item>
                )}

                {/* 邮箱验证 */}
                {renderEmailVerification()}

                {/* 提交按钮 */}
                {renderSubmitButtons(handleDaytourFinalSubmit)}
              </Form>
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  // ─── 标准团确认信息表单（完全不变）──────────────────────────────────────────
  const renderStandardConfirmForm = () => (
    <div className="confirmation-section">
      <Row gutter={24}>
        <Col xs={24} md={10}>
          <div className="passport-image-section">
            <h3>护照照片</h3>
            {tempImageUrl && (
              <Image
                src={tempImageUrl}
                alt="护照照片"
                style={{ width: '100%', maxHeight: '400px', objectFit: 'contain' }}
              />
            )}
          </div>
        </Col>
        <Col xs={24} md={14}>
          <div className="passport-info-section">
            <h3>
              <EditOutlined /> 请确认或修改护照信息
            </h3>
            {nameMismatchWarning && (
              <Alert
                type="warning"
                showIcon
                message="姓名不一致提醒"
                description={
                  <div>
                    <div>销售录入姓名：<strong>{nameMismatchWarning.originalName}</strong></div>
                    <div>护照识别姓名：<strong>{nameMismatchWarning.ocrName}</strong></div>
                    <div style={{ marginTop: 8, color: '#666' }}>
                      请核对护照信息是否正确。如果护照姓名与销售录入的不一致，请联系销售人员确认。
                    </div>
                  </div>
                }
                style={{ marginBottom: 16 }}
              />
            )}
            <Form
              form={form}
              layout="vertical"
              className="passport-form"
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="surnameInput"
                    label="姓 / Surname"
                    rules={[{ required: true, message: '请输入姓' }]}
                  >
                    <Input placeholder="例: ZHANG" onChange={handleSurnameChange} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="givenNameInput"
                    label="名 / Given Name"
                    rules={[{ required: true, message: '请输入名' }]}
                  >
                    <Input placeholder="例: SAN" onChange={handleGivenNameChange} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="fullName" hidden><Input /></Form.Item>

              <Form.Item
                name="passportNumber"
                label="护照号码"
                rules={[{ required: true, message: '请输入护照号码' }]}
              >
                <Input placeholder="请输入护照号码" style={{ textTransform: 'uppercase' }} />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="gender"
                    label="性别"
                    rules={[{ required: true, message: '请选择性别' }]}
                  >
                    <Select>
                      <Option value="M">男</Option>
                      <Option value="F">女</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="nationality"
                    label="国籍"
                    rules={[{ required: true, message: '请选择国籍' }]}
                  >
                    <Select
                      placeholder="请选择国籍"
                      showSearch
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {getAllCountries().map(country => (
                        <Option key={country.value} value={country.value} label={country.label}>
                          {country.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="birthDate"
                    label="出生日期"
                    rules={[{ required: true, message: '请输入出生日期' }]}
                  >
                    <Input placeholder="DD/MM/YYYY" disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="issueDate"
                    label="护照签发日期"
                  >
                    <Input placeholder="DD/MM/YYYY" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="expiryDate"
                label="护照有效期"
                rules={[{ required: true, message: '请输入护照有效期' }]}
              >
                <Input
                  placeholder="DD/MM/YYYY"
                  onChange={(e) => checkPassportExpiry(e.target.value)}
                />
              </Form.Item>
              {passportExpiryWarning && (
                <Tag color="warning" style={{ marginBottom: 16, marginTop: -8, cursor: 'pointer' }} onClick={() => setExpiryModalVisible(true)}>
                  <WarningOutlined /> 护照有效期不足6个月（剩余 {passportExpiryWarning.monthsDiff} 个月）
                </Tag>
              )}
              <Modal
                title={<span><WarningOutlined style={{ color: '#fa8c16', marginRight: 8 }} />护照有效期不足6个月</span>}
                open={expiryModalVisible}
                onCancel={() => setExpiryModalVisible(false)}
                footer={[
                  <Button key="back" onClick={() => setExpiryModalVisible(false)}>
                    返回修改
                  </Button>,
                  <Button key="continue" type="primary" style={{ backgroundColor: '#fa8c16', borderColor: '#fa8c16' }} onClick={() => setExpiryModalVisible(false)}>
                    我已知悉，继续提交
                  </Button>,
                ]}
              >
                {passportExpiryWarning && (
                  <div>
                    <p style={{ fontSize: 15, lineHeight: 1.8 }}>
                      护照有效期（<strong>{passportExpiryWarning.expiryDate}</strong>）距离出团日期（<strong>{passportExpiryWarning.departureDate}</strong>）
                      仅剩 <strong style={{ color: '#ff4d4f' }}>{passportExpiryWarning.monthsDiff} 个月</strong>，不足6个月。
                    </p>
                    <p style={{ fontSize: 14, color: '#666', lineHeight: 1.8 }}>
                      大多数国家要求护照有效期在出发日期后至少6个月以上。建议及时更新护照，以免影响出行。
                    </p>
                    <p style={{ fontSize: 14, color: '#999', lineHeight: 1.8 }}>
                      您仍然可以提交当前护照信息，但系统会定期向销售人员发送提醒邮件，直到护照信息更新为止。
                    </p>
                  </div>
                )}
              </Modal>

              <Form.Item
                name="birthPlace"
                label="出生地"
                rules={[
                  { required: true, message: '请输入出生地' },
                  {
                    pattern: /^[A-Za-z\s-]+$/,
                    message: '出生地只能包含英文字母、空格和横线'
                  }
                ]}
                extra="请使用英文格式，例如：BEIJING"
              >
                <Input
                  placeholder="请输入出生地（英文）"
                  style={{ textTransform: 'uppercase' }}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    form.setFieldsValue({ birthPlace: value });
                  }}
                />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="contactPhone"
                    label="联系电话"
                    rules={[{ required: true, message: '请输入联系电话' }]}
                  >
                    <Input placeholder="请输入联系电话" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="contactEmail"
                    label="联系邮箱（选填）"
                    rules={[
                      { type: 'email', message: '邮箱格式不正确' }
                    ]}
                  >
                    <Input
                      placeholder="请输入联系邮箱"
                      suffix={
                        emailVerified ?
                          <CheckCircleOutlined style={{ color: '#52c41a' }} /> :
                          null
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* 邮箱验证码 */}
              <Row gutter={[16, 0]} align="bottom">
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="verificationCode"
                    label="邮箱验证码"
                  >
                    <Input
                      placeholder="请输入6位验证码"
                      maxLength={6}
                      disabled={emailVerified}
                      suffix={
                        emailVerified ?
                          <CheckCircleOutlined style={{ color: '#52c41a' }} /> :
                          null
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label=" " className="email-verify-actions">
                    <Space wrap>
                      <Button
                        onClick={handleSendVerificationCode}
                        loading={sendingCode}
                        disabled={countdown > 0 || emailVerified}
                      >
                        {countdown > 0 ? `${countdown}s后重发` : '发送验证码'}
                      </Button>
                      <Button
                        type="primary"
                        onClick={handleVerifyCode}
                        loading={verifyingCode}
                        disabled={emailVerified}
                      >
                        验证
                      </Button>
                    </Space>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item className="submit-buttons">
                <Space size="middle" wrap>
                  <Button
                    type="primary"
                    size="large"
                    icon={<CheckCircleOutlined />}
                    onClick={handleFinalSubmit}
                    loading={confirming}
                    disabled={form.getFieldValue('contactEmail') && !emailVerified}
                  >
                    {(!form.getFieldValue('contactEmail') || emailVerified) ? '确认无误，提交信息' : '请先验证邮箱'}
                  </Button>
                  <Button
                    size="large"
                    icon={<ReloadOutlined />}
                    onClick={handleReset}
                  >
                    重新上传
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        </Col>
      </Row>
    </div>
  );

  // ─── 上传区进度条内容（通用） ────────────────────────────────────────────────
  const renderUploadingContent = (processingText) => (
    <div>
      <Spin size="large" />
      <p className="ant-upload-text">{progressText || processingText}</p>
      <div style={{ margin: '20px 40px' }}>
        <Progress
          percent={Math.round(uploadProgress)}
          status="active"
          strokeColor={{
            '0%': '#108ee9',
            '100%': '#52c41a',
          }}
          format={percent => `${percent}%`}
        />
      </div>
      <p className="ant-upload-hint">{progressText || 'AI智能识别中，请稍候...'}</p>
    </div>
  );

  // ─── 中国团附加证件上传（第 3 步）────────────────────────────────────────────
  const renderChinaDocStep = () => {
    const label = chinaDocState?.docLabel || '证件';
    const isId = chinaDocState?.docType === 'china_id';
    return (
      <div className="china-doc-step">
        <Alert
          message={`还需上传${label}`}
          description={
            isId
              ? '本次行程前往中国，您持中国护照，按要求需要再上传一张【身份证人像面】（有姓名和身份证号那一面）照片。'
              : '本次行程前往中国，您持台湾护照，按要求需要再上传一张【台胞证】（台湾居民来往大陆通行证）资料页照片。'
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {!chinaDocPreviewUrl ? (
          <Dragger
            name="chinaDoc"
            multiple={false}
            accept="image/*"
            showUploadList={false}
            beforeUpload={(file) => { handleChinaDocSelect(file); return false; }}
            className="upload-dragger"
            disabled={chinaDocRecognizing}
          >
            {chinaDocRecognizing ? (
              <div>
                <Spin size="large" />
                <p className="ant-upload-text">正在识别{label}…</p>
              </div>
            ) : (
              <>
                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                <p className="ant-upload-text">点击或拖拽{label}照片到此区域</p>
                <p className="ant-upload-hint">支持 JPG/PNG 格式，自动识别证件信息</p>
              </>
            )}
          </Dragger>
        ) : (
          <>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <Image src={chinaDocPreviewUrl} alt={label} style={{ maxHeight: 220 }} />
            </div>

            {chinaDocRecognizing && (
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <Spin /> <span style={{ marginLeft: 8 }}>正在识别{label}…</span>
              </div>
            )}

            {chinaDocWarnings.length > 0 && (
              <Alert
                message="请再核对一下"
                description={
                  <ul style={{ paddingLeft: 20, margin: 0 }}>
                    {chinaDocWarnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                }
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            <Form form={chinaDocForm} layout="vertical" disabled={chinaDocRecognizing}>
              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="fullName"
                    label={`${label}姓名`}
                    rules={[{ required: true, message: `请输入${label}上的姓名` }]}
                  >
                    <Input placeholder="请输入证件上的中文姓名" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="documentNumber"
                    label={isId ? '身份证号码' : '台胞证号码'}
                    rules={[{ required: true, message: '请输入证件号码' }]}
                  >
                    <Input placeholder={isId ? '18位身份证号码' : '台胞证号码'} />
                  </Form.Item>
                </Col>
                {!isId && (
                  <Col xs={24} sm={12}>
                    <Form.Item name="expiryDate" label="有效期至（选填）">
                      <Input placeholder="DD/MM/YYYY" />
                    </Form.Item>
                  </Col>
                )}
              </Row>

              <Form.Item className="submit-buttons">
                <Space size="middle" wrap>
                  <Button
                    type="primary"
                    size="large"
                    loading={chinaDocSubmitting}
                    disabled={chinaDocRecognizing}
                    onClick={handleChinaDocSubmit}
                  >
                    确认并提交{label}
                  </Button>
                  <Button size="large" onClick={handleChinaDocReset} disabled={chinaDocSubmitting}>
                    重新上传照片
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </>
        )}
      </div>
    );
  };

  // ─── 主渲染 ───────────────────────────────────────────────────────────────────
  return (
    <div className="upload-page">
      <Card className="upload-card">
        <h1>{isDaytour ? '证件上传' : '护照上传'}</h1>

        <div className="info-section">
          <p><strong>旅游产品：</strong>{touristInfo.tourName}</p>
          <p><strong>出团日期：</strong>{new Date(touristInfo.departureDate).toLocaleDateString('zh-CN')}</p>
          <p><strong>游客姓名：</strong>{touristInfo.touristName}</p>
          <p><strong>销售姓名：</strong>{touristInfo.salesName}</p>
        </div>

        <Steps current={currentStep} className="upload-steps">
          <Step title={isDaytour ? '上传证件' : '上传护照'} />
          <Step title="确认信息" />
          {needChinaDocStep && <Step title={`上传${chinaDocState.docLabel}`} />}
          <Step title="完成" />
        </Steps>

        {/* ── Step 0：上传 ── */}
        {currentStep === 0 && (
          <>
            {/* 日游专有：支持证件类型说明 */}
            {isDaytour && (
              <div className="doc-type-section">
                <p className="doc-type-hint">
                  支持上传：{DOCUMENT_TYPE_OPTIONS.map(opt => opt.label).join('、')}
                </p>
              </div>
            )}

            <Dragger
              {...(isDaytour ? daytourUploadProps : uploadProps)}
              className="upload-dragger"
              disabled={uploading}
            >
              {uploading ? (
                renderUploadingContent(isDaytour ? '正在处理证件图片...' : '正在处理护照图片...')
              ) : (
                <>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    {isDaytour ? '点击或拖拽证件照片到此区域' : '点击或拖拽护照照片到此区域'}
                  </p>
                  <p className="ant-upload-hint">
                    支持 JPG/PNG 格式，自动识别{isDaytour ? '证件' : '护照'}信息
                  </p>
                </>
              )}
            </Dragger>

            <Alert
              message="上传说明"
              description={
                <div>
                  <ol style={{ fontSize: '13px', paddingLeft: '20px', margin: 0 }}>
                    <li style={{ marginBottom: '8px' }}>
                      <strong>照片清晰完整：</strong>请确保上传的{isDaytour ? '证件' : '护照'}照片清晰可见，文字、号码{isDaytour ? '' : '和头像部分'}不得模糊。
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                      <strong>信息完整不裁切：</strong>{isDaytour ? '证件上所有文字、号码需完整拍入，不要被画面边缘裁掉。' : '护照信息页所有字段，以及最底部两行机读码（MRZ），都要完整拍入画面、不被裁切或手指遮挡（轻微倾斜不影响）。'}
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                      <strong>无反光无阴影：</strong>拍摄或扫描时请避免灯光反射、水波纹或阴影影响，确保页面内容清晰。
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                      <strong>原件直拍：</strong>请上传{isDaytour ? '证件' : '护照'}原件的实拍照片或扫描件，不得上传翻拍屏幕或复印件照片。
                    </li>
                    <li style={{ marginBottom: 0 }}>
                      <strong>正确方向：</strong>请保持{isDaytour ? '证件' : '护照'}页面正向上传，避免倒置或倾斜。
                    </li>
                  </ol>
                </div>
              }
              type="info"
              showIcon
              className="upload-tips"
            />
          </>
        )}

        {/* ── Step 1：确认信息 - 始终挂载以确保Form.Item注册 ── */}
        <div style={{ display: currentStep === 1 && isEditing ? 'block' : 'none' }}>
          {isDaytour ? renderDaytourConfirmForm() : renderStandardConfirmForm()}
        </div>

        {/* ── Step 2：中国团附加证件（身份证 / 台胞证）── */}
        {needChinaDocStep && currentStep === 2 && !finalSubmitted && renderChinaDocStep()}

        {/* 护照有效期弹窗（日游护照类型也复用） */}
        <Modal
          title={<span><WarningOutlined style={{ color: '#fa8c16', marginRight: 8 }} />护照有效期不足6个月</span>}
          open={isDaytour && expiryModalVisible}
          onCancel={() => setExpiryModalVisible(false)}
          footer={[
            <Button key="back" onClick={() => setExpiryModalVisible(false)}>
              返回修改
            </Button>,
            <Button key="continue" type="primary" style={{ backgroundColor: '#fa8c16', borderColor: '#fa8c16' }} onClick={() => setExpiryModalVisible(false)}>
              我已知悉，继续提交
            </Button>,
          ]}
        >
          {passportExpiryWarning && (
            <div>
              <p style={{ fontSize: 15, lineHeight: 1.8 }}>
                护照有效期（<strong>{passportExpiryWarning.expiryDate}</strong>）距离出团日期（<strong>{passportExpiryWarning.departureDate}</strong>）
                仅剩 <strong style={{ color: '#ff4d4f' }}>{passportExpiryWarning.monthsDiff} 个月</strong>，不足6个月。
              </p>
              <p style={{ fontSize: 14, color: '#666', lineHeight: 1.8 }}>
                大多数国家要求护照有效期在出发日期后至少6个月以上。建议及时更新护照，以免影响出行。
              </p>
              <p style={{ fontSize: 14, color: '#999', lineHeight: 1.8 }}>
                您仍然可以提交当前护照信息，但系统会定期向销售人员发送提醒邮件，直到护照信息更新为止。
              </p>
            </div>
          )}
        </Modal>

        <Footer />
      </Card>
    </div>
  );
}

export default UploadPage;
